import classNames from 'classnames';
import React, {
  useState, useContext, useCallback, useMemo,
} from 'react';
import { connect } from 'react-redux';

import {
  getMedia,
  getMediaPresentationStyle,
  getPrimaryMediaType,
  renderCaption,
  renderDek,
} from 'lib/articleUtils';
import { BreakpointContext } from 'lib/ContextTypes';

import { ShowBlogHeroHeadline } from './ShowBlogHeroHeadline';

import './styles.themed.scss';

const block = 'showBlog-hero';

/**
 * Maps Redux state to component props
 * @param {object} params - Redux state
 * @param {object} params.shared - Shared state containing social media profiles
 * @returns {object} Props from state
 */
const mapStateToProps = ({ shared }) => ({
  socialMediaProfiles: shared.socialMediaProfiles,
});

/**
 * ShowBlogHero component renders the hero section for show blog pages
 * @param {object} props - Component props
 * @param {object} props.content - Video/media content object
 * @param {string} props.gridBodyClasses - Grid CSS classes
 * @param {Array} props.headlineClassName - Headline CSS classes
 * @param {boolean} props.isChromeless - Whether in chromeless mode
 * @param {string} props.path - Current page path
 * @param {object} props.socialMediaProfiles - Social media profile data
 * @param {string} props.vertical - Current vertical
 * @returns {React.Element} Rendered hero section
 */
const ShowBlogHero = ({
  content,
  gridBodyClasses = '',
  headlineClassName = [],
  isChromeless = false,
  path = '',
  socialMediaProfiles,
  vertical,
}) => {
  const [hasStickyVideo, setStickyVideoFlag] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const breakpointContext = useContext(BreakpointContext);
  const { isSorM: isMobile = false } = breakpointContext;

  /**
   * Sets the video playing state
   */
  const setVideoPlayState = useCallback(() => {
    setIsVideoPlaying(true);
  }, []);

  // Create context object for utility functions
  const contextForUtils = useMemo(() => ({
    content,
    block,
    hasStickyVideo,
    isChromeless,
    isMobile,
    isVideoPlaying,
    socialMediaProfiles,
    vertical,
    isShowBlog: true,
    setStickyVideoFlag,
    setVideoPlayState,
  }), [
    content,
    hasStickyVideo,
    isChromeless,
    isMobile,
    isVideoPlaying,
    socialMediaProfiles,
    vertical,
    setVideoPlayState,
  ]);

  const mediaPresentationStyle = getMediaPresentationStyle(contextForUtils);
  const mediaType = getPrimaryMediaType(contextForUtils);
  const media = getMedia(contextForUtils);

  const containerClasses = classNames(
    `${block}__container`,
    'layout-grid-container',
    [mediaPresentationStyle],
    {
      [`${block}__sticky-video`]: hasStickyVideo,
    },
  );

  return (
    <section
      className={containerClasses}
      data-test={block}
      data-testid={block}
    >
      <div
        className={classNames(
          gridBodyClasses,
          'showBlog-hero__subcontainer',
        )}
      >
        <div
          className={classNames(
            `${block}__media-holder`,
            {
              mb7: mediaType === null || mediaType === 'image',
              'mb5 mb5-m': mediaType === 'video',
            },
          )}
          data-testid={`${block}__media-holder`}
        >
          {media && (
            <div
              className={classNames(
                `${block}__media-container`,
              )}
            >
              {media}
            </div>
          )}
          {renderCaption(contextForUtils)}
        </div>
        <header
          className={classNames(
            `${block}__header`,
            'mh0-print',
          )}
          data-test={`${block}__header`}
          data-testid={`${block}__header`}
        >
          <ShowBlogHeroHeadline
            className={classNames(
              `${block}__headline`,
              headlineClassName,
            )}
            dek={renderDek(contextForUtils)}
            path={path}
            vertical={vertical}
          >
            {content?.headline?.primary}
          </ShowBlogHeroHeadline>
        </header>
      </div>
    </section>
  );
};

export default connect(mapStateToProps)(ShowBlogHero);
